import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useNotes, useCreateNote } from '~/hooks/use-notes';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';

export function NotesPage() {
  const notes = useNotes();
  const create = useCreateNote();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await create.mutateAsync({ title, content: content || undefined });
    setTitle('');
    setContent('');
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-950">
      <div className="max-w-xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Записки</h1>
          <Link to="/">
            <Button variant="ghost" size="sm">← На главную</Button>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Новая запись</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Заголовок"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-slate-600"
                maxLength={200}
                required
              />
              <textarea
                placeholder="Текст (необязательно)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full h-24 px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-900 text-sm resize-none placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-slate-600"
                maxLength={10_000}
              />
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={create.isPending || !title.trim()}>
                  {create.isPending ? 'Сохраняю…' : 'Сохранить'}
                </Button>
                {create.error && (
                  <span className="text-sm text-red-600 dark:text-red-400">Ошибка: {create.error.message}</span>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {notes.isPending && <div className="text-sm text-slate-500 dark:text-slate-400">Загрузка…</div>}
          {notes.error && (
            <div className="text-sm text-red-600 dark:text-red-400">Ошибка: {notes.error.message}</div>
          )}
          {notes.data && notes.data.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-400">Пока нет записей.</div>
          )}
          {notes.data?.map((note) => (
            <Card key={note.id}>
              <CardContent className="py-4">
                <div className="font-medium text-slate-900 dark:text-slate-100">{note.title}</div>
                {note.content && (
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap dark:text-slate-400">{note.content}</div>
                )}
                <div className="text-xs text-slate-400 mt-2 dark:text-slate-500">
                  {new Date(note.createdAt).toLocaleString('ru')}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
